import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wand2, RotateCcw, Trash2, Image as ImageIcon, Sparkles } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { useIllustrations, ILLUSTRATION_KEYS } from "@/context/IllustrationsContext";

import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function AdminIllustrations() {
  const { lang } = useLang();
  const { map, refresh } = useIllustrations();
  const [busy, setBusy] = useState(null);
  const [prompts, setPrompts] = useState({});

  const currentPrompt = (item) => prompts[item.key] ?? item.prompt;

  const generate = async (item) => {
    setBusy(item.key);
    try {
      await api.post("/admin/illustrations/generate", {
        key: item.key,
        prompt: currentPrompt(item),
      });
      toast.success(lang === "es" ? "Imagen generada" : "Image generated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (key) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar imagen?" : "Delete image?")) return;
    await api.delete(`/admin/illustrations/${key}`);
    toast.success(lang === "es" ? "Eliminada" : "Deleted");
    refresh();
  };

  const generateAll = async () => {
    for (const item of ILLUSTRATION_KEYS) {
      if (map[item.key]) continue;
      await generate(item);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl">{lang === "es" ? "Ilustraciones AI" : "AI Illustrations"}</h1>
          <p className="font-body text-neutral-700">
            {lang === "es"
              ? "Genera arte 4K con Gemini Nano Banana para reemplazar los SVG placeholders."
              : "Generate 4K art with Gemini Nano Banana to replace SVG placeholders."}
          </p>
        </div>
        <Button
          onClick={generateAll}
          data-testid="illu-generate-all"
          className="brutal-btn bg-canales-red text-white px-4 py-2"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {lang === "es" ? "Generar todas las faltantes" : "Generate all missing"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ILLUSTRATION_KEYS.map((item) => {
          const current = map[item.key];
          const isBusy = busy === item.key;
          return (
            <div
              key={item.key}
              data-testid={`illu-card-${item.key}`}
              className="brutal p-4 bg-white flex flex-col"
            >
              <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                {item.key}
              </div>
              <h3 className="font-display font-black text-lg">{item.label}</h3>

              <div className="mt-3 border-2 border-black rounded-lg overflow-hidden bg-canales-cream aspect-square flex items-center justify-center relative">
                {current ? (
                  <img
                    src={`data:${current.mime_type};base64,${current.data}`}
                    alt={item.label}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center p-4">
                    <ImageIcon className="w-10 h-10 mx-auto text-neutral-400" />
                    <p className="mt-2 text-sm font-body text-neutral-500">
                      {lang === "es" ? "Sin generar" : "Not generated"}
                    </p>
                  </div>
                )}
                {isBusy && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-white font-display font-black animate-pulse">
                      {lang === "es" ? "Generando…" : "Generating…"}
                    </div>
                  </div>
                )}
              </div>

              <Textarea
                value={currentPrompt(item)}
                onChange={(e) => setPrompts((p) => ({ ...p, [item.key]: e.target.value }))}
                className="mt-3 text-xs"
                rows={4}
              />

              <div className="mt-3 flex gap-2">
                <Button
                  onClick={() => generate(item)}
                  disabled={isBusy}
                  data-testid={`illu-gen-${item.key}`}
                  className="brutal-btn bg-canales-blue text-white px-3 py-1 text-sm flex-1"
                >
                  <Wand2 className="w-4 h-4 mr-1" />
                  {current ? (lang === "es" ? "Regenerar" : "Regenerate") : (lang === "es" ? "Generar" : "Generate")}
                </Button>
                {current && (
                  <Button
                    onClick={() => remove(item.key)}
                    className="brutal-btn bg-red-500 text-white px-3 py-1 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
